<template>
  <div>
    <base-header class="pb-6">
      <b-row align-v="center" class="py-4">
        <b-col cols="7" lg="6">
          <h6 class="h2 text-white d-inline-block mb-0">{{ title }}</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </b-col>
        <b-col lg="6" cols="5" class="text-right">
          <base-button size="sm" type="neutral" @click="cancel">Cancel</base-button>
        </b-col>
      </b-row>
    </base-header>
    <b-container fluid class="mt--6">
      <validation-observer v-slot="{ invalid }" ref="formValidator">
        <form class="needs-validation" ref="transactionForm"
              @submit.prevent="submit">
          <b-row class="py-4">
            <b-col md="4" class="pt-6">
              <h4>General Information</h4>
            </b-col>
            <b-col>
              <b-card no-body>
                <b-card-body>
                  <base-input label="Status">
                    <el-select v-model="form.status" placeholder="Select a Status">
                      <el-option-group label="Listing Statuses">
                        <el-option value="coming-soon" label="Coming Soon"></el-option>
                        <el-option value="active" label="Active"></el-option>
                        <el-option value="cancelled" label="Cancelled"></el-option>
                        <el-option value="expired" label="Expired"></el-option>
                      </el-option-group>
                      <el-option-group label="Transaction Statuses">
                        <el-option value="pending" label="Pending"></el-option>
                        <el-option value="closed" label="Closed"></el-option>
                        <el-option value="failed" label="Failed"></el-option>
                      </el-option-group>
                    </el-select>
                  </base-input>

                  <base-input label="Sale Type" :rules="{required: { transactionStatus: true } }"  name="Sale Type" v-if="transactionStatus">
                      <el-select v-model="form.sale_type" placeholder="Select a Type">
                        <el-option value="Listing" label="Listing"></el-option>
                        <el-option value="Buyer" label="Buyer"></el-option>
                      </el-select>
                  </base-input>

                  <base-input label="Transaction Type:" rules="required" name="Transaction Type">
                    <el-select v-model="form.transaction_type" placeholder="Select a Type">
                      <el-option value="Resale" label="Resale"></el-option>
                      <el-option value="New Construction" label="New Construction"></el-option>
                      <el-option value="Referral In" label="Referral In"></el-option>
                      <el-option value="Referral Out" label="Referral Out"></el-option>
                      <el-option value="Lease" label="Lease"></el-option>
                      <el-option value="Lot" label="Lot"></el-option>
                      <el-option value="Commercial" label="Commercial"></el-option>
                    </el-select>
                  </base-input>

                  <base-input label="Do you want this transaction to count towards your annual unit goal?" label-for="units">
                    <base-switch class="mr-1" v-model="form.units"></base-switch>
                  </base-input>

                  <div v-if="listingStatus">
                    <base-input label-for="listing_date">
                      <label class="form-control-label">Listing Date: <span id="listing-date"><i class="fas fa-question-circle"></i></span></label>
                      <b-tooltip target="listing-date" triggers="hover" variant="danger">
                        Date that the listing agreement was signed.
                      </b-tooltip>
                      <b-form-datepicker v-model="form.listing_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>

                    <base-input label-for="target_mls_date">
                      <label class="form-control-label">Target MLS Date: <span id="target-mls-date"><i class="fas fa-question-circle"></i></span></label>
                      <b-tooltip target="target-mls-date" triggers="hover" variant="danger">
                        Date the listing is projected to go in the MLS
                      </b-tooltip>
                      <b-form-datepicker v-model="form.target_mls_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>

                    <base-input label-for="active_date" v-if="form.status != 'coming-soon'">
                        <label class="form-control-label">Active Date: <span id="active-date"><i class="fas fa-question-circle"></i></span></label>
                        <b-tooltip target="active-date" triggers="hover" variant="danger">
                          The date the listing went active on MLS
                        </b-tooltip>
                      <b-form-datepicker v-model="form.active_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>

                    <base-input label="Expiration Date:" label-for="expiration_date">
                      <b-form-datepicker v-model="form.expiration_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>
                  </div>

                  <div v-if="transactionStatus">
                    <base-input label-for="contract_date">
                        <label class="form-control-label">Ratified Contract Date: <span id="contract-date"><i class="fas fa-question-circle"></i></span></label>
                        <b-tooltip target="contract-date" triggers="hover" variant="danger">
                          What date was the purchase agreement ratified by buyer and seller?
                        </b-tooltip>
                      <b-form-datepicker v-model="form.contract_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>

                    <base-input label="Contract Estimated Close Date:" label-for="close_date">
                      <b-form-datepicker v-model="form.close_date" required placeholder="Choose a date" local="en"></b-form-datepicker>
                    </base-input>
                  </div>

                  <base-input label="Is this transaction contingent on another sale?" label-for="contingent_sale">
                    <base-switch class="mr-1" v-model="form.contingent_sale"></base-switch>
                  </base-input>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="py-4">
            <b-col md="4">
              <h4>Client Information</h4>
            </b-col>
            <b-col>
              <b-card no-body>
                <b-card-body>
                  <b-row>
                    <b-col md="4">
                      <base-input label="Client Name"
                                  name="Client Name"
                                  rules="required"
                                  v-model="form.client.name">
                      </base-input>
                    </b-col>
                    <b-col md="4">
                      <base-input label="Client Phone"
                                  name="Client Phone"
                                  v-model="form.client.phone">
                      </base-input>
                    </b-col>
                    <b-col md="4">
                      <base-input label="Client Email"
                                  name="Client Email"
                                  v-model="form.client.email">
                      </base-input>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col md="3">
                      <base-input label="Address"
                                  name="Address"
                                  rules="required"
                                  v-model="form.property.address">
                      </base-input>
                    </b-col>
                    <b-col md="3">
                      <base-input label="City"
                                  name="City"
                                  rules="required"
                                  v-model="form.property.city">
                      </base-input>
                    </b-col>
                    <b-col md="3">
                      <base-input label="State/Province"
                                  name="State/Province"
                                  rules="required"
                                  v-model="form.property.state">
                      </base-input>
                    </b-col>
                    <b-col md="3">
                      <base-input label="Postal Code"
                                  name="Postal Code"
                                  rules="required"
                                  v-model="form.property.zipcode">
                      </base-input>
                    </b-col>
                  </b-row>

                  <base-input label="Primary Source">
                    <el-select v-model="form.primary_source"
                               filterable
                               placeholder="i.e. Database, Zillow">
                      <el-option v-for="(source, key) in primarySources"
                                 :key="source"
                                 :label="source"
                                 :value="source">
                      </el-option>
                    </el-select>
                  </base-input>

                  <base-input label="Database Sub Source" v-if="form.primary_source == 'Database'">
                    <el-select v-model="form.source"
                               filterable
                               allow-create
                               placeholder="i.e. Past Client, SOI, Vendor Referral">
                      <el-option v-for="(source, key) in sources"
                                 :key="source"
                                 :label="source"
                                 :value="source">
                      </el-option>
                    </el-select>
                  </base-input>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="py-4">
            <b-col md="4">
              <h4>Income</h4>
            </b-col>
            <b-col>
              <b-card no-body>
                <b-card-body>
                  <base-input label="Sale Price:"
                              type="number"
                              step="0.01"
                              min="0"
                              input-group-classes="input-group-merge"
                              :rules="{ required: { transactionStatus: true }, min_value:1 }"
                              name="Sale Price"
                              prependIcon="fas fa-dollar-sign"
                              v-model="form.sale_price"
                              v-if="transactionStatus">
                  </base-input>

                  <base-input label="Listing Price"
                              type="number"
                              step="0.01"
                              min="0"
                              input-group-classes="input-group-merge"
                              :rules="{ required: { listingStatus: true }, min_value:1 }"
                              name="Listing Price"
                              prependIcon="fas fa-dollar-sign"
                              v-model="form.listing_price"
                              v-if="listingStatus">
                  </base-input>

                  <div>
                    <label class="form-control-label" v-if="listingStatus">Commission Charged to Seller:</label>
                    <label class="form-control-label" v-else>Commission:</label>
                  </div>
                  <b-row>
                    <b-col>
                      <base-input label-for="commission_type">
                        <b-form-radio-group v-model="form.commission_type" name="commission_type" button-variant="outline-danger" buttons :required="transactionStatus">
                          <b-form-radio value="0" name="Percentage"><i class="fas fa-percent ml-0"></i></b-form-radio>
                          <b-form-radio value="1" name="Flat Rate"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                        </b-form-radio-group>
                      </base-input>
                    </b-col>
                    <b-col>
                      <base-input label=""
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  input-group-classes="input-group-merge"
                                  name="Commission Amount"
                                  v-model="form.amount">
                        <template #prepend v-if="form.commission_type == 1"><i class="fas fa-dollar-sign"></i></template>
                        <template #append v-if="form.commission_type == 0"><i class="fas fa-percent"></i></template>
                      </base-input>
                    </b-col>
                  </b-row>
                  <div v-if="listingStatus">
                    <label class="form-control-label">Commission offered to Cooperating Broker:</label>
                    <b-row>
                      <b-col md="3">
                        <base-input label="" label-for="broker_commission_type">
                          <b-form-radio-group v-model="form.broker_commission_type" button-variant="outline-danger" buttons name="broker_commission_type">
                            <b-form-radio value="0" checked><i class="fas fa-percent ml-0"></i></b-form-radio>
                            <b-form-radio value="1"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                          </b-form-radio-group>
                        </base-input>
                      </b-col>
                      <b-col>
                        <base-input label=""
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    input-group-classes="input-group-merge"
                                    name="Broker Commission Amount"
                                    v-model="form.broker_commission_amount"
                                    :helperText="'Broker Commission: ' + brokerCommission | currency">
                          <template #prepend v-if="form.broker_commission_type == 1"><i class="fas fa-dollar-sign"></i></template>
                          <template #append v-if="form.broker_commission_type == 0"><i class="fas fa-percent"></i></template>
                        </base-input>
                      </b-col>
                    </b-row>
                  </div>

                  <base-input label="Transaction Fee:" 
                              label-for="transaction_fee"
                              type="number"
                              step="0.01"
                              min="0"
                              input-group-classes="input-group-merge"
                              prependIcon="fas fa-dollar-sign"
                              v-model="form.transaction_fee">
                  </base-input>
                      
                  <p class="form-control-label"><strong>Total Commission:</strong> {{ commission | currency }}</p>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="py-4">
            <b-col md="4">
              <h4>Cost of Sale</h4>
            </b-col>
            <b-col>
              <b-card no-body>
                <b-card-body>
                    <h3>Who Is Getting Paid On This Transaction?</h3>

                    <base-input label="Who's sale is this?" label-for="agent_id">
                      <el-select v-model="form.agent_id">
                        <el-option v-for="member in members"
                                   :key="member.id"
                                   :label="member.name"
                                   :value="member.id">
                        </el-option>
                      </el-select>
                    </base-input>

                    <base-input label="Are you paying a referral fee on this sale?" label-for="referral_fee">
                      <base-switch class="mr-1" v-model="form.referral_fee"></base-switch>
                    </base-input>

                    <b-row v-if="form.referral_fee == 1">
                      <b-col>
                        <base-input label="Referral Type:" label-for="referral_type">
                          <b-form-radio-group v-model="form.referral_type" button-variant="outline-danger" buttons name="referral_type">
                            <b-form-radio value="0" checked><i class="fas fa-percent ml-0"></i></b-form-radio>
                            <b-form-radio value="1"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                          </b-form-radio-group>
                        </base-input>
                      </b-col>
                      <b-col>
                        <base-input label="Referral Fee Amount:"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    input-group-classes="input-group-merge"
                                    name="Referral Feel Amount"
                                    v-model="form.referral_amount">
                          <template #prepend v-if="form.referral_type == 1"><i class="fas fa-dollar-sign"></i></template>
                          <template #append v-if="form.referral_type == 0"><i class="fas fa-percent"></i></template>
                          <template #helperText><small class="text-muted">Referral Fee: {{ referralFee | currency }}</small></template>
                        </base-input>
                      </b-col>
                    </b-row>

                    <span>Team Member Compensation:</span>
                    <p class="small text-danger"><em>NOTE: ONLY INCLUDE TEAM MEMBERS THAT WILL RECEIVE COMPENSATION ON THE SALE. IF YOU ARE THE OWNER OF THE TEAM AND IT IS YOUR SALE, DO NOT ADD YOURSELF AS RECEIVING A SPLIT.</em></p>
                    <b-row v-for="(member, index) in form.members" :key="index">
                      <b-col md="4">
                        <label class="form-control-label">Member:</label>
                        <b-input-group label-for="member">
                          <template #prepend>
                            <b-button size="sm" variant="danger" @click="removeMember(index)"><i class="fas fa-trash px-2"></i></b-button>
                          </template>
                          <b-form-select v-model="member.id">
                            <b-form-select-option :value="member.id" v-for="member in members" :key="member.id">{{ member.name }}</b-form-select-option>
                          </b-form-select>
                        </b-input-group>
                      </b-col>
                      <b-col md="4">
                        <base-input label="Commission Type:" label-for="type">
                          <b-form-radio-group v-model="member.commission_type" :name="'member-' + member.id" button-variant="outline-danger" buttons>
                            <b-form-radio value="0" checked><i class="fas fa-percent ml-0"></i></b-form-radio>
                            <b-form-radio value="1"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                          </b-form-radio-group>
                        </base-input>
                      </b-col>
                      <b-col md="4">
                        <base-input type="number"
                                    step="0.01"
                                    min="0"
                                    input-group-classes="input-group-merge"
                                    label="Commission Amount:" 
                                    label-for="amount"
                                    v-model="member.amount">
                          <template #prepend v-if="member.commission_type == 1"><i class="fas fa-dollar-sign"></i></template>
                          <template #append v-if="member.commission_type == 0"><i class="fas fa-percent"></i></template>
                          <template #helperText>
                            <small class="text-muted" v-if="member.commission_type == 0">Commission: {{ (commission - referralFee) * (member.amount / 100) | currency }}</small>
                            <small class="text-muted" v-else>Commission: {{ member.amount | currency }}</small>
                          </template>
                        </base-input>
                      </b-col>
                    </b-row>

                    <base-input>
                      <b-button size="sm" variant="primary" @click="addMember">Add member</b-button>
                    </base-input>

                    <base-input label="Are you paying your broker a split on this sale?" label-for="broker_paid">
                      <base-switch class="mr-1" v-model="form.broker_paid"></base-switch>
                    </base-input>

                    <b-row v-if="form.broker_paid == 1">
                      <b-col md="6">
                        <base-input label="Broker Type:" label-for="broker_type">
                          <b-form-radio-group v-model="form.broker_type" name="broker_type" button-variant="outline-danger" buttons>
                            <b-form-radio value="0" checked><i class="fas fa-percent ml-0"></i></b-form-radio>
                            <b-form-radio value="1"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                          </b-form-radio-group>
                        </base-input>
                      </b-col>
                      <b-col md="6">
                        <base-input type="number"
                                    step="0.01"
                                    min="0"
                                    input-group-classes="input-group-merge"
                                    label="Broker Amount:" 
                                    label-for="broker_amount"
                                    v-model="form.broker_amount">
                          <template #prepend v-if="form.broker_type == 1"><i class="fas fa-dollar-sign"></i></template>
                          <template #append v-if="form.broker_type == 0"><i class="fas fa-percent"></i></template>
                          <template #helperText><small class="text-muted">Broker Split: {{ brokerTotal | currency }}</small></template>
                        </base-input>
                      </b-col>
                    </b-row>

                    <base-input label="Are you paying a Franchise Fee on this transaction?" label-for="franchise_fee_paid">
                      <base-switch class="mr-1" v-model="form.franchise_fee_paid"></base-switch>
                    </base-input>

                    <b-row v-if="form.franchise_fee_paid == 1">
                      <b-col md="6">
                        <base-input label="Franchise Fee Type:" label-for="franchise_fee_type">
                          <b-form-radio-group v-model="form.franchise_fee_type" name="franchise_fee_type" button-variant="outline-danger" buttons>
                            <b-form-radio value="0" checked><i class="fas fa-percent ml-0"></i></b-form-radio>
                            <b-form-radio value="1"><i class="fas fa-dollar-sign ml-0"></i></b-form-radio>
                          </b-form-radio-group>
                        </base-input>
                      </b-col>
                      <b-col md="6">
                        <base-input type="number"
                                    step="0.01"
                                    min="0"
                                    input-group-classes="input-group-merge"
                                    label="Franchise Fee Amount:" 
                                    label-for="franchise_fee_amount"
                                    v-model="form.franchise_fee_amount">
                          <template #prepend v-if="form.franchise_fee_type == 1"><i class="fas fa-dollar-sign"></i></template>
                          <template #append v-if="form.franchise_fee_type == 0"><i class="fas fa-percent"></i></template>
                          <template #helperText><small class="text-muted">Franchise Fee: {{ franchiseFeeTotal | currency }}</small></template>
                        </base-input>
                      </b-col>
                    </b-row>

                    <base-input label-for="other_cos" 
                                class="py-1"
                                input-group-classes="input-group-merge"
                                type="number"
                                step="0.01"
                                min="0"
                                v-model="form.other_cos">
                      <template #label>
                        <label class="form-control-label">Additional Cost of Sale: <span id="other-cos"><i class="fas fa-question-circle"></i></span></label>
                        <b-tooltip target="other-cos" triggers="hover" variant="danger">
                          Are there any other costs you will incur when this transaction closes? I.E. Paying for a Home Warranty at closing, paying for inspection repairs at closing, etc.?
                        </b-tooltip>
                      </template>
                      <template #prepend><i class="fas fa-dollar-sign"></i></template>
                    </base-input>

                    <p class="form-control-label"><strong>Total Commission:</strong> {{ commission | currency }}</p>
                    <p class="form-control-label"><strong>Total Cost of Sale:</strong> {{ cosTotal | currency }}</p>
                    <p class="form-control-label"><strong>Gross Profit:</strong> {{ grossProfit | currency}}</p>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="py-4">
            <b-col md="4">
              <h4>Vendor Information</h4>
            </b-col>
            <b-col>
              <b-card no-body>
                <b-card-body>
                  <p><strong>What companies are associated with this transaction?</strong></p>
                  <b-row v-for="(company, index) in form.companies" :key="index">
                    <b-col md="6">
                      <base-input label="Company Name:" label-for="company">
                        <b-input-group>
                          <template #prepend>
                            <b-button size="sm" variant="danger" @click="removeCompany(index)">Remove</b-button>
                          </template>
                          <b-form-input v-model="company.name" type="text"></b-form-input>
                        </b-input-group>
                      </base-input>
                    </b-col>
                    <b-col md="6">
                      <base-input label="Company Type:" label-for="amount" rules="required" name="Company Type">
                        <el-select v-model="company.type" required>
                          <el-option label="Select a Type" :value="null"></el-option>
                          <el-option :value="0" label="Mortgage Company"></el-option>
                          <el-option :value="1" label="Title Company"></el-option>
                          <el-option :value="2" label="Law Firm"></el-option>
                          <el-option :value="3" label="Home Inspection"></el-option>
                          <el-option :value="4" label="Home Insurance"></el-option>
                          <el-option :value="6" label="Photography"></el-option>
                          <el-option :value="7" label="Contractor"></el-option>
                          <el-option :value="8" label="Stager"></el-option>
                          <el-option :value="9" label="Other"></el-option>
                        </el-select>
                      </base-input>
                    </b-col>
                  </b-row>

                  <base-input>
                    <b-button variant="primary" @click="addCompany">Add Company</b-button>
                  </base-input>

                  <p><strong>Would you like to add a Loan Officer, Escrow Officer, or an Attorney?</strong></p>
                  <b-row v-if="officers">
                    <b-col md="6">
                      <base-input label="Loan Officer:" label-for="loan_officer">
                        <b-form-input v-model="form.loan_officer" type="text"></b-form-input>
                      </base-input>
                    </b-col>
                    <b-col md="6">
                      <base-input label="Escrow Officer/Attorney Name:" label-for="escrow_officer">
                        <b-form-input v-model="form.escrow_officer" type="text"></b-form-input>
                      </base-input>
                    </b-col>
                  </b-row>

                  <base-input v-if="!officers">
                    <b-button variant="primary" @click="officers = true">Add Officers</b-button>
                  </base-input>
                </b-card-body>
              </b-card>

              <ul v-if="errorList" class="text-danger">
                <li v-for="(error, index) in errorList" :key="index">{{ error }}</li>
              </ul>

              <base-button block type="success" native-type="submit">Submit</base-button>
            </b-col>
          </b-row>
        </form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import {Select, OptionGroup, Option} from 'element-ui';
import initialState from './FormData/transactionFormData';
import errorsMixin from '@/mixins/errors-mixin';
import sourcesMixin from '@/mixins/sources-mixin';

export default {
  mixins: [errorsMixin, sourcesMixin],
  components: {
    RouteBreadCrumb,
    [Select.name]: Select,
    [OptionGroup.name]: OptionGroup,
    [Option.name]: Option
  },
  props: {
    transaction: null,
  },
  data() {
    return initialState();
  },
  computed: {
    title() {
      return 'Transfer Pipeline';
    },
    userRoute() {
      if(this.$route.params.user) {
        return process.env.VUE_APP_API_BASE_URL + "/users/" + this.$route.params.user;
      }
      return this.$store.getters['profile/userRoute'];
    },
    listingStatus() {
      return this.listings.includes(this.form.status);
    },
    transactionStatus() {
      return this.transactions.includes(this.form.status);
    },
    commission() {
      if(this.form.commission_type == 1) {
        if(this.listingStatus) {
          return Number(this.form.amount) - this.brokerCommission;
        } else {
          return Number(this.form.amount);
        }
      } else {
        if(this.listingStatus) {
          return (Number(this.form.listing_price) * (Number(this.form.amount) / 100) - this.brokerCommission);
        }

        return Number(this.form.sale_price) * (Number(this.form.amount) / 100);
      }
    },
    grossProfit() {
      return this.commission - this.cosTotal + Number(this.form.transaction_fee);
    },
    brokerCommission() {
      if(this.form.broker_commission_type == 1) {
        return Number(this.form.broker_commission_amount);
      } else {
        return Number(this.form.listing_price) * (Number(this.form.broker_commission_amount) / 100);
      }
    },
    brokerTotal() {
      if(this.form.broker_type == true) {
        return Number(this.form.broker_amount);
      } else {
        return (this.commission - this.referralFee - this.agentFees) * (this.form.broker_amount / 100);
      }
    },
    franchiseFeeTotal() {
      if(this.form.franchise_fee_type == true) {
        return Number(this.form.franchise_fee_amount);
      } else {
        return (this.commission - this.referralFee - this.agentFees) * (this.form.franchise_fee_amount / 100);
      }
    },
    referralFee() {
      if(this.form.referral_type == true) {
        return Number(this.form.referral_amount);
      } else {
        return (this.commission) * (this.form.referral_amount / 100);
      }
    },
    cosTotal() {
      const referralFee = this.referralFee;
      const brokerTotal = this.brokerTotal;
      const franchiseFeeTotal = this.franchiseFeeTotal;
      const agentFees = this.agentFees;

      return referralFee + brokerTotal + franchiseFeeTotal + Number(this.form.other_cos) + agentFees;
    },
    agentFees() {
      let commission = 0;
      const commissionTotal = this.commission - this.referralFee;
      const referralFee = this.referralFee;

      Object.values(this.form.members).forEach(member => {
        if(member.commission_type == 1) {
          commission = commission + Number(member.amount);
        } else {
          commission = commission + (commissionTotal * (Number(member.amount) / 100));
        }
      });

      return commission;
    }
  },
  methods: {
    async submit() {
      this.errorList = null;
      const isValid = await this.$refs.formValidator.validate();

      if(!isValid) {
        this.errorList = Object.values(this.$refs.formValidator.errors).flat();
      } else {
        this.add();
      }
    },
    async add() {
      axios.post(this.userRoute + "/transactions", this.form)
      .then(response => {
        Object.assign(this.$data, initialState());
        this.$refs.formValidator.reset();
        this.deleteRow(this.$route.params.id);
      })
      .catch(error => {
        this.error(error);
      });
    },
    deleteRow(id) {
      axios.delete(this.userRoute + "/pipelines/" + id)
      .then(response => {
        swal.fire({
          title: 'Success!',
          text: `Your pipeline has been transfered successfully.`,
          type: 'success',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success'
        }).then(result => {
          this.$router.back();
        });
      })
      .catch(error => {
        this.error(error);
      });
    },
    cancel() {
      Object.assign(this.$data, initialState());
      this.$router.back();
    },
    getClients() {
      axios.get(this.userRoute + "/clients")
      .then(response => {
        this.clients = response.data;
      })
      .catch(error => {
        this.error(error);
      });
    },
    getMembers() {
      axios.get(this.userRoute + "/members/team?active=true")
      .then(response => {
        this.members = response.data;
      })
      .catch(error => {
        this.error(error);
      });
    },
    addMember() {
      this.form.members.push({
        id: "",
        amount: 0,
        commission_type: 0
      });
    },
    removeMember(index) {
      this.form.members.splice(index, 1);
    },
    addCompany() {
      this.form.companies.push({
        id: "",
        name: "",
        type: ""
      });
    },
    removeCompany(index) {
      this.form.companies.splice(index, 1);
    },
    async getPipeline() {
      axios.get(this.userRoute + '/pipelines/' + this.$route.params.id)
      .then(response => {
        this.form.sale_type = response.data.type.charAt(0).toUpperCase() + response.data.type.slice(1);
        this.form.listing_price = response.data.price;
        this.form.sale_price = response.data.price;
        this.form.primary_source = response.data.primary_source;
        this.form.source = response.data.source;
        this.form.client = response.data.client;
        this.form.property = response.data.property;
      })
      .catch(error => {
        this.error(error);
      });
    },
  },
  created() {
    this.getClients();
    this.getMembers();
    this.getPipeline();
  },
  mounted() {
    document.title = this.title + " | ClarityNOW";
  },
  watch: {
    'form.referral_fee'(value) {
      if(value == false) {
        this.form.referral_amount = 0;
      }
    },
    'form.broker_paid'(value) {
      if(value == false) {
        this.form.broker_amount = 0;
      }
    },
    'form.franchise_fee_paid'(value) {
      if(value == false) {
        this.form.franchise_fee_amount = 0;
      }
    }
  },
}
</script>